import axios from 'axios';

// Support both REACT_APP_API_URL (new) and REACT_APP_BACKEND_URL (legacy)
const BACKEND_URL = process.env.REACT_APP_API_URL || process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/admin`;

// Get auth token from localStorage
const getAuthToken = () => {
  return localStorage.getItem('admin_token');
};

// Set auth header
const getAuthHeaders = () => {
  const token = getAuthToken();
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

// Admin Authentication
export const adminLogin = async (username, password) => {
  const response = await axios.post(`${API}/login`, { username, password });
  if (response.data.access_token) {
    localStorage.setItem('admin_token', response.data.access_token);
  }
  return response.data;
};

export const adminLogout = () => {
  localStorage.removeItem('admin_token');
};

export const isAdminAuthenticated = () => {
  return !!getAuthToken();
};

export const getAdminToken = () => {
  return getAuthToken();
};

// Dashboard
export const getDashboardStats = async () => {
  const response = await axios.get(`${API}/dashboard/stats`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// Orders
export const getOrders = async (status = null, skip = 0, limit = 50) => {
  const params = { skip, limit };
  if (status) params.status = status;
  
  const response = await axios.get(`${API}/orders`, {
    headers: getAuthHeaders(),
    params
  });
  return response.data;
};

export const getOrderDetails = async (orderId) => {
  const response = await axios.get(`${API}/orders/${orderId}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const updateOrderStatus = async (orderId, orderStatus, notes = null) => {
  const response = await axios.put(
    `${API}/orders/${orderId}/status`,
    { order_status: orderStatus, notes },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

// Services
export const getAdminServices = async () => {
  const response = await axios.get(`${API}/services`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const createService = async (token, serviceData) => {
  const response = await axios.post(`${API}/services`, serviceData, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });
  return response.data;
};

export const updateService = async (token, serviceId, updateData) => {
  const response = await axios.put(
    `${API}/services/${serviceId}`,
    updateData,
    { 
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }
  );
  return response.data;
};

export const deleteService = async (token, serviceId) => {
  const response = await axios.delete(`${API}/services/${serviceId}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });
  return response.data;
};

// Portfolio
export const getAdminPortfolio = async () => {
  const response = await axios.get(`${API}/portfolio`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const createPortfolio = async (token, projectData) => {
  const response = await axios.post(`${API}/portfolio`, projectData, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });
  return response.data;
};

export const updatePortfolio = async (token, projectId, updateData) => {
  const response = await axios.put(
    `${API}/portfolio/${projectId}`,
    updateData,
    { 
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }
  );
  return response.data;
};

export const deletePortfolio = async (token, projectId) => {
  const response = await axios.delete(`${API}/portfolio/${projectId}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });
  return response.data;
};

// Testimonials
export const getAdminTestimonials = async () => {
  const response = await axios.get(`${API}/testimonials`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const createTestimonial = async (token, testimonialData) => {
  const response = await axios.post(`${API}/testimonials`, testimonialData, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });
  return response.data;
};

export const updateTestimonial = async (token, testimonialId, updateData) => {
  const response = await axios.put(
    `${API}/testimonials/${testimonialId}`,
    updateData,
    { 
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }
  );
  return response.data;
};

export const deleteTestimonial = async (token, testimonialId) => {
  const response = await axios.delete(`${API}/testimonials/${testimonialId}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });
  return response.data;
};

// Contact Messages
export const getContactMessages = async (status = null, skip = 0, limit = 50) => {
  const params = { skip, limit };
  if (status) params.status = status;
  
  const response = await axios.get(`${API}/messages`, {
    headers: getAuthHeaders(),
    params
  });
  return response.data;
};

export const updateMessageStatus = async (messageId, status, response = null) => {
  const res = await axios.put(
    `${API}/messages/${messageId}/status`,
    { status, response },
    { headers: getAuthHeaders() }
  );
  return res.data;
};

// Admin User Management
export const getAdminUsers = async (token) => {
  const response = await axios.get(`${API}/users`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });
  return response.data;
};

export const createAdminUser = async (token, userData) => {
  const response = await axios.post(`${API}/users`, userData, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });
  return response.data;
};

export const updateAdminUser = async (token, adminId, updateData) => {
  const response = await axios.put(`${API}/users/${adminId}`, updateData, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });
  return response.data;
};

export const deleteAdminUser = async (token, adminId) => {
  const response = await axios.delete(`${API}/users/${adminId}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });
  return response.data;
};

// Client Management (for admin)
export const getClients = async () => {
  const response = await axios.get(`${API}/clients`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

